/**
 * SparkApproval Component
 * 
 * Renders an animated Spark for circuit/governance approvals.
 * User scans with Seeker to sign and submit approval.
 */

import { useEffect, useRef, useState } from 'react';
import './SparkApproval.css';

const SPARK_SERVER = 'https://spark.link';
const EDGE_URL = import.meta.env.VITE_EDGE_URL || 'https://edge.estream.dev';

interface SparkApprovalProps {
  circuitId: string;
  circuitType: string;
  description: string;
  onApproved?: () => void;
  onClose?: () => void;
}

interface SparkRegistration {
  code: string;
  url: string;
  expires_at: number;
}

export default function SparkApproval({ 
  circuitId, 
  circuitType, 
  description,
  onApproved,
  onClose 
}: SparkApprovalProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number | null>(null);
  const [status, setStatus] = useState<'loading' | 'ready' | 'approved' | 'error'>('loading');
  const [sparkCode, setSparkCode] = useState<string | null>(null);
  const [error] = useState<string | null>(null);
  
  // Particle state for animation
  const particlesRef = useRef<Particle[]>([]);
  const startTimeRef = useRef<number>(Date.now());

  // Register Spark
  useEffect(() => {
    registerSpark();
  }, [circuitId]);
  
  // Start animation after canvas is ready
  useEffect(() => {
    if (status !== 'ready' || !canvasRef.current || particlesRef.current.length === 0) {
      return;
    }
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    const size = 200;
    canvas.width = size;
    canvas.height = size;
    const center = size / 2;
    const particles = particlesRef.current;
    
    startTimeRef.current = Date.now();
    
    const animate = () => {
      const elapsed = Date.now() - startTimeRef.current;
      const hue = (elapsed / 50) % 360;
      const pulse = 0.5 + Math.sin(elapsed / 500) * 0.3;
      
      // Clear
      ctx.fillStyle = '#0a0a0f';
      ctx.fillRect(0, 0, size, size);
      
      // Orbit ring
      ctx.beginPath();
      ctx.arc(center, center, size * 0.35, 0, Math.PI * 2);
      ctx.strokeStyle = `hsla(${hue}, 60%, 50%, 0.2)`;
      ctx.lineWidth = 2;
      ctx.stroke();
      
      // Center glow
      const gradient = ctx.createRadialGradient(center, center, 0, center, center, 40);
      gradient.addColorStop(0, `hsla(${hue}, 80%, 70%, ${pulse})`);
      gradient.addColorStop(0.5, `hsla(${hue}, 70%, 50%, ${pulse * 0.5})`);
      gradient.addColorStop(1, 'transparent');
      ctx.fillStyle = gradient;
      ctx.beginPath();
      ctx.arc(center, center, 40, 0, Math.PI * 2);
      ctx.fill();
      
      // Particles
      particles.forEach((p) => {
        const angle = (p.phase + elapsed * p.speed * 0.001) * (Math.PI / 180);
        const x = center + Math.cos(angle) * (size * p.radius);
        const y = center + Math.sin(angle) * (size * p.radius);
        
        // Glow
        const pGrad = ctx.createRadialGradient(x, y, 0, x, y, 15);
        pGrad.addColorStop(0, `hsla(${p.hue}, 80%, 70%, 0.8)`);
        pGrad.addColorStop(0.5, `hsla(${p.hue}, 70%, 50%, 0.4)`);
        pGrad.addColorStop(1, 'transparent');
        ctx.fillStyle = pGrad;
        ctx.beginPath();
        ctx.arc(x, y, 15, 0, Math.PI * 2);
        ctx.fill();
        
        // Core
        ctx.beginPath();
        ctx.arc(x, y, 5, 0, Math.PI * 2);
        ctx.fillStyle = '#ffffff';
        ctx.fill();
      });
      
      // Label
      ctx.font = '10px system-ui, sans-serif';
      ctx.fillStyle = `hsla(${hue}, 60%, 70%, 0.8)`;
      ctx.textAlign = 'center';
      ctx.fillText('Scan with Seeker', center, size - 15);
      
      animationRef.current = requestAnimationFrame(animate);
    };
    
    animate();
    
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [status]);

  // Poll for approval status
  useEffect(() => {
    if (status !== 'ready' || !sparkCode) return;
    
    const pollInterval = setInterval(async () => {
      try {
        const response = await fetch(`${EDGE_URL}/api/circuits/${circuitId}`);
        if (response.ok) {
          const data = await response.json();
          if (data.signatures?.length > 0) {
            setStatus('approved');
            onApproved?.();
          }
        }
      } catch (e) {
        // Ignore polling errors
      }
    }, 2000);
    
    return () => clearInterval(pollInterval);
  }, [status, sparkCode, circuitId, onApproved]);

  const registerSpark = async () => {
    try {
      // Create Spark payload for circuit approval
      const now = Math.floor(Date.now() / 1000);
      const nonce = generateNonce();
      
      const payload = {
        version: 1,
        code: '', // Will be assigned by server
        data: {
          type: 'circuit-approval',
          circuit_id: circuitId,
          circuit_type: circuitType,
          description: description,
          action: 'approve',
          callback_url: `${EDGE_URL}/api/circuits/approve`,
        },
        created_at: now,
        expires_at: now + 300, // 5 minutes
        nonce: nonce,
      };

      // For dev mode, we'll use a simplified registration
      // In production, this would include ML-DSA-87 signature
      const response = await fetch(`${SPARK_SERVER}/api/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          pubkey: 'dev-console-key', // Would be real key in production
          payload,
          signature: 'dev-signature', // Would be ML-DSA-87 signature
        }),
      });

      if (!response.ok) {
        // Fallback: use local-only Spark (no server registration)
        const localCode = generateLocalCode();
        setSparkCode(localCode);
        initParticles(localCode);
        setStatus('ready');
        
        // Store in ws-edge for Seeker to resolve
        await registerLocalSpark(localCode, payload);
        return;
      }

      const data = await response.json() as SparkRegistration;
      setSparkCode(data.code);
      initParticles(data.code);
      setStatus('ready');
      
    } catch (e) {
      console.error('[SparkApproval] Registration failed:', e);
      
      // Fallback to local Spark
      const localCode = generateLocalCode();
      setSparkCode(localCode);
      initParticles(localCode);
      setStatus('ready');
      
      // Register with ws-edge
      await registerLocalSpark(localCode, {
        type: 'circuit-approval',
        circuit_id: circuitId,
        circuit_type: circuitType,
        description: description,
      });
    }
  };

  const registerLocalSpark = async (code: string, payload: unknown) => {
    try {
      await fetch(`${EDGE_URL}/spark/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          code,
          payload,
          expires_at: Date.now() + 300000, // 5 minutes
        }),
      });
    } catch (e) {
      console.warn('[SparkApproval] Local registration failed:', e);
    }
  };

  const initParticles = (code: string) => {
    // Derive particles from code for deterministic animation
    const seed = hashCode(code);
    particlesRef.current = Array.from({ length: 8 }, (_, i) => ({
      id: i,
      phase: (seed + i * 47) % 360,
      speed: 0.5 + (((seed * (i + 1)) % 100) / 200),
      radius: 0.25 + (((seed * (i + 3)) % 50) / 200),
      hue: (seed + i * 45) % 360,
    }));
  };

  if (status === 'approved') {
    return (
      <div className="spark-approval approved">
        <div className="spark-success">
          <span className="success-icon">✓</span>
          <h3>Approved!</h3>
          <p>Circuit {circuitId.slice(0, 12)} has been signed.</p>
        </div>
        <button className="btn btn-primary" onClick={onClose}>Done</button>
      </div>
    );
  }

  return (
    <div className="spark-approval">
      <div className="spark-header">
        <h3>Approve Circuit</h3>
        <button className="close-btn" onClick={onClose}>×</button>
      </div>
      
      <div className="spark-content">
        <div className="spark-canvas-container">
          <canvas ref={canvasRef} className="spark-canvas" />
          {status === 'loading' && (
            <div className="spark-loading">Generating...</div>
          )}
        </div>
        
        <div className="spark-info">
          <div className="info-row">
            <span className="label">Circuit:</span>
            <code>{circuitId}</code>
          </div>
          <div className="info-row">
            <span className="label">Type:</span>
            <span className="badge badge-warning">{circuitType}</span>
          </div>
          <div className="info-row">
            <span className="label">Action:</span>
            <span>{description}</span>
          </div>
        </div>
        
        {sparkCode && (
          <div className="spark-code">
            <span className="code-label">Spark Code:</span>
            <code>{sparkCode}</code>
          </div>
        )}
        
        <p className="spark-instructions">
          Open Seeker and scan this Spark to approve the circuit.
          Your signature will be recorded on the lattice.
        </p>
      </div>
      
      {error && <div className="spark-error">{error}</div>}
    </div>
  );
}

// ============================================================================
// Utilities
// ============================================================================

interface Particle {
  id: number;
  phase: number;
  speed: number;
  radius: number;
  hue: number;
}

function generateNonce(): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  return Array.from(bytes).map(b => b.toString(16).padStart(2, '0')).join('');
}

function generateLocalCode(): string {
  const alphabet = '123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz';
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  return Array.from(bytes).map(b => alphabet[b % alphabet.length]).join('');
}

function hashCode(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) - hash) + str.charCodeAt(i);
    hash = hash & hash;
  }
  return Math.abs(hash);
}
